import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Calendar, Gift, LogIn, Tag, UserPlus } from 'lucide-react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { clubsService } from '../../services';
import { useUser } from '../../UserContext';
import './ConnectPerks.css';

const formatDate = (value) => {
    if (!value) return null;
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const normalizeUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
};

const PublicPerkPage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { initialized, isAuthenticated } = useUser();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [perk, setPerk] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadPerk = async () => {
            setIsLoading(true);
            try {
                const data = await clubsService.getSharedOffer(token);
                setPerk(data);
                setError('');
            } catch (loadError) {
                setError(loadError.message || 'Unable to load this shared perk.');
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            loadPerk();
        }
    }, [token]);

    useEffect(() => {
        const resolve = async () => {
            if (!initialized || !isAuthenticated || !token) return;
            try {
                const data = await clubsService.resolveSharedOffer(token);
                if (data?.dashboardUrl) {
                    navigate(data.dashboardUrl, { replace: true });
                }
            } catch {
                // Leave on public page if resolve fails.
            }
        };

        resolve();
    }, [initialized, isAuthenticated, navigate, token]);

    return (
        <div className="connect-perks-page">
            <Header openLoginModal={() => setIsLoginModalOpen(true)} isAuthenticated={initialized ? isAuthenticated : null} alwaysSolid />
            <main className="connect-perks-main">
                <div className="container">
                    <Link className="connect-perks-back" to="/"><ArrowLeft size={16} /> Back home</Link>

                    {isLoading ? (
                        <section className="connect-perks-state"><Gift size={20} /><p>Loading shared perk...</p></section>
                    ) : error || !perk ? (
                        <section className="connect-perks-state"><Gift size={20} /><p>{error || 'This perk is not available.'}</p></section>
                    ) : (
                        <section className="connect-perks-hero">
                            <div>
                                <span className="connect-perks-pill">Shared Perk</span>
                                <h1>{perk.title}</h1>
                                <p>{perk.fullDescription || perk.shortDescription || 'Live perk on Shoot Connect.'}</p>
                                <div className="connect-perk-meta" style={{ marginTop: 16 }}>
                                    <em>{perk.clubName}</em>
                                    <small>{perk.audience || 'Public'} · {perk.perkSource}</small>
                                </div>
                                <div className="connect-perk-meta" style={{ marginTop: 12 }}>
                                    {perk.offerType ? <small><Tag size={14} /> {perk.offerType}</small> : null}
                                    {perk.endDate ? <small><Calendar size={14} /> Ends {formatDate(perk.endDate)}</small> : null}
                                </div>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 24 }}>
                                    <button className="connect-perk-cta" type="button" onClick={() => setIsLoginModalOpen(true)}><LogIn size={16} /> Log In</button>
                                    <Link className="connect-perk-cta" to="/register"><UserPlus size={16} /> Sign Up</Link>
                                    {perk.perkSource === 'Supplier' && perk.ctaUrl ? (
                                        <a href={normalizeUrl(perk.ctaUrl)} className="connect-perk-cta" target="_blank" rel="noopener noreferrer">
                                            {perk.ctaLabel || 'Open supplier offer'}
                                        </a>
                                    ) : perk.publicVenueUrl ? (
                                        <Link className="connect-perk-cta" to={perk.publicVenueUrl}>Open venue page</Link>
                                    ) : null}
                                </div>
                            </div>
                        </section>
                    )}
                </div>
            </main>
            <Footer />
            <LoginModal isOpen={isLoginModalOpen} onClose={() => setIsLoginModalOpen(false)} />
        </div>
    );
};

export default PublicPerkPage;
